"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useTranslations } from "next-intl";

import type { Tournament } from "@/entities/tournament/model/types";
import { useAuthStore } from "@/shared/model/auth-store";
import { useUserProfile } from "@/hooks/useAuth";
import { handleError, extractApiErrorMessage } from "@/shared/api/error-handler";
import {
  getTempTeamInvitePath,
  reissueTempTeamInvitePath,
} from "@/shared/api/temp-team-invite";
import { api } from "@/shared/lib/api";
import { Button } from "@/shared/ui/buttons/button";
import { Card, CardContent } from "@/shared/ui/cards/card";
import { Input } from "@/shared/ui/inputs/input";
import { Label } from "@/shared/ui/forms/label";
import { Switch } from "@/shared/ui/inputs/switch";

type TempTeamListItem = {
  teamId: string;
  tournamentId: string;
  name: string;
  tag: string;
  captainId: string;
  captainName: string;
  isLookingForPartner: boolean;
  memberCount: number;
  maxMembers: number;
  createdAt: string;
};

type JoinRequest = {
  id: string;
  tournamentId: string;
  teamId: string;
  requesterUserId: string;
  requesterName: string;
  message?: string | null;
  status: string;
  requestedAt: string;
};

export function TournamentTempTeamsPanel({
  tournament,
}: {
  tournament: Tournament;
}) {
  const tUi = useTranslations("Ui");
  const tToast = useTranslations("Toasts");
  const t = useTranslations("Tournaments");
  const isAuth = useAuthStore((s) => s.isAuthenticated);
  const { data: user } = useUserProfile();

  const teamSize = tournament.maxTeamSize ?? 5;
  const isClassic = tournament.format === "Classic";
  // KAN-110: Arena (кроме Random — там составы собирает менеджер) использует
  // те же temp-команды, но панель была жёстко закрыта на format === "Classic",
  // из-за чего созданный temp-ростер не отображался на странице турнира.
  const isArenaPreMade =
    tournament.format === "Arena" &&
    (tournament as { arenaTeamMode?: string }).arenaTeamMode !== "Random";
  // KAN-339: временные команды расширены с 2–4 до 2–5 (для 5×5 раньше была вторая,
  // недописанная реализация — сквады, которые не попадали в сетку).
  const isTempTeamsMode =
    (isClassic || isArenaPreMade) && teamSize > 1 && teamSize <= 5;
  const isSoloClassic = isClassic && teamSize === 1;

  const [myTeamId, setMyTeamId] = useState<string>("");
  const [myTeamName, setMyTeamName] = useState<string>("");
  const [looking, setLooking] = useState(false);

  // KAN-339: состояние блока приглашений (ссылка + по нику).
  const [inviteNickname, setInviteNickname] = useState<string>("");
  const [inviteBusy, setInviteBusy] = useState(false);

  const [openTeams, setOpenTeams] = useState<TempTeamListItem[]>([]);
  const [requests, setRequests] = useState<JoinRequest[]>([]);
  const [requestedTeamIds, setRequestedTeamIds] = useState<
    Record<string, true>
  >({});
  const [sendingJoinTeamId, setSendingJoinTeamId] = useState<string>("");

  const [loadingTeams, setLoadingTeams] = useState(false);
  const [savingMyTeam, setSavingMyTeam] = useState(false);
  const [loadingRequests, setLoadingRequests] = useState(false);

  const canUse = isAuth && user?.id;

  const refreshOpenTeams = useCallback(async () => {
    setLoadingTeams(true);
    try {
      const res = await api.get(
        `/TournamentTeams/tournaments/${tournament.id}/temp-teams`,
      );
      setOpenTeams(res.data ?? []);
    } catch (err) {
      handleError(err, {
        component: "TournamentTempTeamsPanel",
        action: "refreshOpenTeams",
        meta: { tournamentId: tournament.id },
      });
    } finally {
      setLoadingTeams(false);
    }
  }, [tournament.id]);

  const refreshRequests = useCallback(
    async (teamId: string) => {
      if (!teamId) return;
      setLoadingRequests(true);
      try {
        const res = await api.get(
          `/TournamentTeams/teams/${teamId}/join-requests`,
        );
        setRequests(res.data ?? []);
      } catch (err) {
        handleError(err, {
          component: "TournamentTempTeamsPanel",
          action: "refreshRequests",
          meta: { tournamentId: tournament.id, teamId },
        });
      } finally {
        setLoadingRequests(false);
      }
    },
    [tournament.id],
  );

  useEffect(() => {
    if (!isTempTeamsMode) return;
    refreshOpenTeams();
  }, [isTempTeamsMode, refreshOpenTeams]);

  // KAN-147 #2: myTeamId держался только в state после POST — после релоада капитан
  // видел «Создать» вместо «Сохранить», заявки скрывались, повторный клик слал второй
  // POST create. Восстанавливаем «мою команду» из списка (я — капитан) при монтировании.
  useEffect(() => {
    if (myTeamId || !user?.id) return;
    const mine = (openTeams ?? []).find((tm) => tm.captainId === user.id);
    if (mine) {
      setMyTeamId(mine.teamId);
      setMyTeamName(mine.name ?? "");
      setLooking(mine.isLookingForPartner);
      void refreshRequests(mine.teamId);
    }
  }, [openTeams, user?.id, myTeamId, refreshRequests]);

  const visibleOpenTeams = useMemo(() => {
    const list = openTeams ?? [];
    return myTeamId ? list.filter((t) => t.teamId !== myTeamId) : list;
  }, [openTeams, myTeamId]);

  // KAN-339: ссылка и приглашение по нику. Токен создаётся бэкендом при первом запросе,
  // поэтому «скопировать» и есть «получить» — отдельной кнопки создания не нужно.
  // KAN-1641: путь приглашения — контракт сервера (shared/api/temp-team-invite),
  // чтобы хук настроек ростера и эта панель не собирали URL по-разному.
  const fetchInviteLink = async (reissue: boolean): Promise<string | null> => {
    if (!myTeamId) return null;
    try {
      const invitePath = reissue
        ? await reissueTempTeamInvitePath(myTeamId)
        : await getTempTeamInvitePath(myTeamId);
      return `${window.location.origin}${invitePath}`;
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data
        ?.message;
      toast.error(msg || tUi("litTournamentTempTeamsPanel.neUdalosPoluchitSsylku"));
      return null;
    }
  };

  const copyInviteLink = async () => {
    if (!myTeamId) return;
    setInviteBusy(true);
    try {
      const url = await fetchInviteLink(false);
      if (!url) return;
      await navigator.clipboard.writeText(url);
      toast.success(tToast("tournamentTempTeamsPanel.ssylkaSkopirovana"));
    } catch {
      toast.error(tToast("tournamentTempTeamsPanel.neUdalosSkopirovatSkopiruyte"));
    } finally {
      setInviteBusy(false);
    }
  };

  const reissueInviteLink = async () => {
    if (!myTeamId) return;
    setInviteBusy(true);
    try {
      const url = await fetchInviteLink(true);
      if (!url) return;
      try {
        await navigator.clipboard.writeText(url);
        toast.success(tToast("tournamentTempTeamsPanel.novayaSsylkaSkopirovanaStaraya"));
      } catch {
        toast.success(tToast("tournamentTempTeamsPanel.novayaSsylkaVypuschenaStaraya"));
      }
    } finally {
      setInviteBusy(false);
    }
  };

  const inviteByNickname = async () => {
    if (!myTeamId || !inviteNickname.trim()) return;
    setInviteBusy(true);
    try {
      await api.post(`/TournamentTeams/teams/${myTeamId}/temp-invite/by-nickname`, {
        nickname: inviteNickname.trim(),
      });
      toast.success(tToast("tournamentTempTeamsPanel.priglashenieOtpravleno"));
      setInviteNickname("");
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data
        ?.message;
      toast.error(msg || tUi("litTournamentTempTeamsPanel.neUdalosOtpravitPriglashenie"));
    } finally {
      setInviteBusy(false);
    }
  };

  const createOrUpdateMyTeam = async () => {
    if (!canUse) {
      toast.error(t("tempTeams.needLogin"));
      return;
    }
    setSavingMyTeam(true);
    try {
      const res = await api.post(
        `/TournamentTeams/tournaments/${tournament.id}/temp-teams`,
        {
          name: myTeamName || undefined,
          isLookingForPartner: looking,
        },
      );
      const id = res.data?.id as string | undefined;
      if (id) {
        setMyTeamId(id);
        toast.success(t("tempTeams.teamSaved"));
        await Promise.all([refreshOpenTeams(), refreshRequests(id)]);
      }
    } catch (err: unknown) {
      toast.error(
        extractApiErrorMessage(err, t("tempTeams.saveError")),
      );
      handleError(err, {
        component: "TournamentTempTeamsPanel",
        action: "createOrUpdateMyTeam",
        meta: { tournamentId: tournament.id },
      });
    } finally {
      setSavingMyTeam(false);
    }
  };

  const sendJoinRequest = async (teamId: string) => {
    if (!canUse) {
      toast.error(t("tempTeams.needLogin"));
      return;
    }
    try {
      setSendingJoinTeamId(teamId);
      await api.post(`/TournamentTeams/teams/${teamId}/join-requests`, {});
      setRequestedTeamIds((prev) => ({ ...prev, [teamId]: true }));
      toast.success(t("tempTeams.requestSent"));
    } catch (err: unknown) {
      const msg = String(extractApiErrorMessage(err, ""));
      if (msg.toLowerCase().includes("request already sent")) {
        setRequestedTeamIds((prev) => ({ ...prev, [teamId]: true }));
        toast.success(t("tempTeams.requestSent"));
        return;
      }

      toast.error(msg || t("tempTeams.sendError"));
      handleError(err, {
        component: "TournamentTempTeamsPanel",
        action: "sendJoinRequest",
        meta: { tournamentId: tournament.id, teamId },
      });
    } finally {
      setSendingJoinTeamId("");
    }
  };

  const accept = async (requestId: string) => {
    try {
      await api.post(`/TournamentTeams/join-requests/${requestId}/accept`, {});
      toast.success(t("tempTeams.accepted"));
      await refreshRequests(myTeamId);
      await refreshOpenTeams();
    } catch (err: unknown) {
      toast.error(extractApiErrorMessage(err, t("tempTeams.acceptError")));
      handleError(err, {
        component: "TournamentTempTeamsPanel",
        action: "acceptJoinRequest",
        requestId,
        meta: { tournamentId: tournament.id },
      });
    }
  };

  const reject = async (requestId: string) => {
    try {
      await api.post(`/TournamentTeams/join-requests/${requestId}/reject`, {});
      toast.success(t("tempTeams.rejected"));
      await refreshRequests(myTeamId);
    } catch (err: unknown) {
      toast.error(extractApiErrorMessage(err, t("tempTeams.rejectError")));
      handleError(err, {
        component: "TournamentTempTeamsPanel",
        action: "rejectJoinRequest",
        requestId,
        meta: { tournamentId: tournament.id },
      });
    }
  };

  if (!isClassic && !isArenaPreMade) return null;

  if (isSoloClassic) {
    return (
      <Card className="mb-6">
        <CardContent className="pt-6 text-sm text-muted-foreground">
          {t("tempTeams.soloInfo")}
        </CardContent>
      </Card>
    );
  }

  if (!isTempTeamsMode) return null;

  return (
    <div className="mb-6 space-y-4">
      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="text-sm font-semibold">
            {t("tempTeams.teamTitle", { size: teamSize })}
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="temp-team-name">{t("tempTeams.nameLabel")}</Label>
              <Input
                id="temp-team-name"
                value={myTeamName}
                onChange={(e) => setMyTeamName(e.target.value)}
                placeholder={t("tempTeams.namePlaceholder")}
              />
            </div>

            <div className="space-y-2">
              <Label>{t("tempTeams.lookingLabel")}</Label>
              <div className="flex items-center gap-3">
                <Switch checked={looking} onCheckedChange={setLooking} />
                <div className="text-sm text-muted-foreground">
                  {t("tempTeams.lookingDesc")}
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button
              onClick={createOrUpdateMyTeam}
              disabled={!canUse || savingMyTeam}
            >
              {myTeamId ? t("tempTeams.save") : t("tempTeams.create")}
            </Button>
            {myTeamId && (
              <Button
                variant="outline"
                onClick={() => window.open(`/rosters/${myTeamId}`, "_blank")}
              >
                {t("tempTeams.openTeam")}
              </Button>
            )}
          </div>

          {/* KAN-339: два дополнительных способа собрать состав помимо заявок из списка —
              ссылка «для своих» и адресное приглашение по нику. Оба идут через один токен. */}
          {myTeamId && (
            <div className="space-y-3 rounded-lg border border-border bg-muted/20 p-3">
              <div className="text-sm font-semibold">{tUi("tournamentTempTeamsPanel.pozvatIgrokov")}</div>

              <div className="flex flex-wrap items-center gap-2">
                <Button variant="outline" size="sm" onClick={copyInviteLink} disabled={inviteBusy}>
                  {inviteBusy ? tUi("litTournamentTempTeamsPanel.gotovim") : tUi("litTournamentTempTeamsPanel.skopirovatSsylku")}
                </Button>
                <Button variant="ghost" size="sm" onClick={reissueInviteLink} disabled={inviteBusy}>
                  {tUi("tournamentTempTeamsPanel.perevypustit")}
                </Button>
                <span className="text-xs text-muted-foreground">
                  {tUi("tournamentTempTeamsPanel.poSsylkeVstupayutSrazu")}
                </span>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <Input
                  value={inviteNickname}
                  onChange={(e) => setInviteNickname(e.target.value)}
                  placeholder={tUi("tournamentTempTeamsPanel.nikIgroka")}
                  className="h-9 w-56"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={inviteByNickname}
                  disabled={inviteBusy || !inviteNickname.trim()}
                >
                  {tUi("tournamentTempTeamsPanel.priglasit")}
                </Button>
                <span className="text-xs text-muted-foreground">
                  {tUi("tournamentTempTeamsPanel.igrokuPridetUvedomlenieSo")}
                </span>
              </div>
            </div>
          )}

          {myTeamId && (
            <div className="space-y-2">
              <div className="text-sm font-semibold">{t("tempTeams.requestsTitle")}</div>
              {loadingRequests ? (
                <div className="text-sm text-muted-foreground">{t("tempTeams.loading")}</div>
              ) : requests.length === 0 ? (
                <div className="text-sm text-muted-foreground">{t("tempTeams.noneYet")}</div>
              ) : (
                <div className="space-y-2">
                  {requests.map((r) => (
                    <div
                      key={r.id}
                      className="flex items-center justify-between gap-3 rounded-md border p-3"
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">
                          {r.requesterName}
                        </div>
                        {r.message && (
                          <div className="text-xs text-muted-foreground truncate">
                            {r.message}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button size="sm" onClick={() => accept(r.id)}>
                          {t("tempTeams.accept")}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => reject(r.id)}
                        >
                          {t("tempTeams.reject")}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6 space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div className="text-sm font-semibold">{t("tempTeams.openTeamsTitle")}</div>
            <Button
              variant="outline"
              size="sm"
              onClick={refreshOpenTeams}
              disabled={loadingTeams}
            >
              {t("tempTeams.refresh")}
            </Button>
          </div>

          {loadingTeams ? (
            <div className="text-sm text-muted-foreground">{t("tempTeams.loading")}</div>
          ) : visibleOpenTeams.length === 0 ? (
            <div className="text-sm text-muted-foreground">{t("tempTeams.noneYet")}</div>
          ) : (
            <div className="space-y-2">
              {visibleOpenTeams.map((team) => (
                <div
                  key={team.teamId}
                  className="flex items-center justify-between gap-3 rounded-md border p-3"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">
                      {team.name} {team.tag}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {team.captainName} • {team.memberCount}/{team.maxMembers}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        window.open(`/rosters/${team.teamId}`, "_blank")
                      }
                    >
                      {t("tempTeams.open")}
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => sendJoinRequest(team.teamId)}
                      disabled={
                        Boolean(requestedTeamIds[team.teamId]) ||
                        sendingJoinTeamId === team.teamId
                      }
                    >
                      {requestedTeamIds[team.teamId]
                        ? t("tempTeams.requestSentBtn")
                        : sendingJoinTeamId === team.teamId
                          ? t("tempTeams.sending")
                          : t("tempTeams.joinBtn")}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
